// Copyright 2015-2023 Nstream, inc.
// All rights reserved.

import type {Value} from "@swim/structure";
import {Uri} from "@swim/uri";
import {MapDownlink} from "@swim/client";
import {TraitModelSet} from "@swim/model";
import {RelationTrait} from "@nstream/domain";
import {LanesEntityTrait} from "./LanesEntityTrait";

/** @public */
export class LanesRelationTrait extends RelationTrait<LanesEntityTrait> {
  constructor() {
    super();
    this.id.set("lane");
    this.title.setIntrinsic("Lanes");
  }

  @TraitModelSet({
    extends: true,
    traitType: LanesEntityTrait,
    initTrait(lanesTrait: LanesEntityTrait): void {
      // Initialize the entity
      const laneId = lanesTrait.id.value;
      lanesTrait.title.setIntrinsic("" + laneId);
      // Configure entity aspects
      lanesTrait.portal.insertModel();
    },
    createTrait(id?: string, laneType?: Value): LanesEntityTrait {
      const lanesTrait = new LanesEntityTrait(laneType!);
      if (id !== void 0) {
        lanesTrait.id.setIntrinsic(id);
      }
      this.createModel(lanesTrait);
      return lanesTrait;
    },
  })
  override readonly entities!: TraitModelSet<this, LanesEntityTrait> & RelationTrait<LanesEntityTrait>["entities"] & {
    /** @override */
    createTrait(id?: string, laneType?: Value): LanesEntityTrait;
  };

  @MapDownlink({
    laneUri: "lanes",
    keyForm: Uri.form(),
    consumed: true,
    didUpdate(nodeUri: Uri, value: Value): void {
      //console.log("LanesRelationTrait.lanes.didUpdate " + nodeUri + ":", value.toLike());
      const laneId = nodeUri.toString();
      let lanesTrait = this.owner.entities.get(laneId);
      if (lanesTrait === null) {
        const laneType = value.get("laneType");
        lanesTrait = this.owner.entities.createTrait(laneId, laneType);

        // LanesEntityTrait inherits its nodeUri
        lanesTrait.laneUri.set(nodeUri);

        this.owner.entities.addTrait(lanesTrait);
      }
    },
  })
  readonly lanes!: MapDownlink<this, Uri, Value>;
}
